import React from 'react';
import { useTranslation } from 'react-i18next';

export interface ErrorBannerProps {
  error: Error | string | null;
  onRetry?: () => void;
  className?: string;
  showRetry?: boolean;
}

/**
 * Unified error banner component with ESG styling
 * Displays errors in a visible, styled banner with optional retry button
 */
export function ErrorBanner({ error, onRetry, className = '', showRetry = true }: ErrorBannerProps) {
  const { t } = useTranslation();

  if (!error) return null;

  const errorMessage = typeof error === 'string' ? error : error.message || 'Unknown error';

  return (
    <div
      className={`border border-red-500/50 bg-red-900/20 rounded-lg p-4 ${className}`}
      role="alert"
      aria-live="assertive"
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 text-red-400 text-xl">⚠️</div>
        <div className="flex-1">
          <div className="text-red-300 font-orbitron text-sm font-semibold mb-1">
            {t('error.title') || 'Error'}
          </div>
          <div className="text-red-200 text-sm">{errorMessage}</div>
        </div>
        {showRetry && onRetry && (
          <button
            onClick={onRetry}
            className="flex-shrink-0 px-3 py-1 text-xs border border-red-500/50 text-red-300 rounded hover:bg-red-500/20 transition-colors font-orbitron"
          >
            {t('error.retry') || 'Retry'}
          </button>
        )}
      </div>
    </div>
  );
}

export default ErrorBanner;






